/* eslint-disable no-nested-ternary */
import { useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  ListItemText,
  Tab,
  Tabs,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';
import { fAPIDate, fDate, fDateTime } from 'src/utils/format-time';
import { getFactoringStatusColor } from 'src/utils/cheque-factoring-helpers';

import { useAuthContext } from 'src/auth/hooks';
import { useAppDispatcher } from 'src/redux/store';
import { AuthPermissions } from 'src/auth/permissions';
import { getChequeByID, getChequePayments, getChequePaymentTotal } from 'src/redux/slices/cheque';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CopyableDetail from 'src/components/copyable-detail';

import MarkClearedDialog from 'src/sections/cheque-factoring/mark-cleared-dialog';
import MarkSettledDialog from 'src/sections/cheque-factoring/mark-settled-dialog';
import MarkReturnedDialog from 'src/sections/cheque-factoring/mark-returned-dialog';

import {
  ChequeCustomerType,
  ChequeStatus,
  FactoringStatus,
  IChequeItem,
  IChequePayment,
} from 'src/types/cheque';

import SettlementTab from './settlement-tab';
import ChequeTimeline from './cheque-timeline';
import AddPaymentDialog from './add-payment-dialog';
import UpdateChequeStatus from './update-cheque-status';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'history', label: 'History' },
  { value: 'payments', label: 'Recovery Payments' },
  { value: 'settlements', label: 'Supplier Settlement' },
];

// Statuses in which a returned cheque can have supplier settlements.
const SETTLEMENT_STATUSES = [
  ChequeStatus.RETURN,
  ChequeStatus.SETTLED,
  ChequeStatus.HANDOVER,
  ChequeStatus.RECOVERING,
  ChequeStatus.RECOVERED,
];

type Props = {
  currentCheque?: IChequeItem;
};

export default function ChequeDetails({ currentCheque }: Props) {
  const [tabValue, setTabValue] = useState('overview');
  const [payments, setPayments] = useState<IChequePayment[]>([]);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [settlementRefreshKey, setSettlementRefreshKey] = useState(0);

  const { user: authUser } = useAuthContext();
  const dispatch = useAppDispatcher();

  const confirm = useBoolean();
  const showPaymentDialog = useBoolean();
  const showClearedDialog = useBoolean();
  const showReturnedDialog = useBoolean();
  const showSettledDialog = useBoolean();

  useEffect(() => {
    const fetchPayments = async () => {
      if (
        tabValue === 'payments' &&
        currentCheque &&
        (currentCheque.status === ChequeStatus.RECOVERING ||
          currentCheque.status === ChequeStatus.RECOVERED)
      ) {
        setLoadingPayments(true);
        try {
          const [paymentsRes, totalRes] = await Promise.all([
            dispatch(getChequePayments({ chequeId: currentCheque._id })).unwrap(),
            dispatch(getChequePaymentTotal({ chequeId: currentCheque._id })).unwrap(),
          ]);
          setPayments(paymentsRes);
          setPaymentTotal(totalRes.totalPaid);
        } catch (error) {
          console.error('Failed to fetch payment data:', error);
          enqueueSnackbar('Failed to load payment data', { variant: 'error' });
        } finally {
          setLoadingPayments(false);
        }
      }
    };

    fetchPayments();
  }, [tabValue, currentCheque, dispatch]);

  const refreshCheque = () => {
    if (currentCheque) {
      dispatch(getChequeByID({ itemID: currentCheque._id }));
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(_, value) => setTabValue(value)}
              sx={{
                px: 2.5,
                boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
              }}
            >
              {TABS.filter((tab) => {
                if (tab.value === 'payments') {
                  return (
                    currentCheque?.status === ChequeStatus.RECOVERING ||
                    currentCheque?.status === ChequeStatus.RECOVERED
                  );
                }
                if (tab.value === 'settlements') {
                  // A held cheque never reached a supplier, so it has nothing to settle.
                  return (
                    !!currentCheque &&
                    !currentCheque.heldAt &&
                    !currentCheque.bankedAt &&
                    SETTLEMENT_STATUSES.includes(currentCheque.status)
                  );
                }
                return true;
              }).map((tab) => (
                <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
              ))}
            </Tabs>

            {tabValue === 'overview' && (
              <Grid container spacing={2} mt={2}>
                <Grid
                  mb={1}
                  display="flex"
                  mt={2}
                  alignItems="center"
                  justifyContent="space-between"
                  width={1}
                >
                  <Typography variant="subtitle1">Basic Information</Typography>

                  {hasPermission(
                    authUser,
                    'any',
                    AuthPermissions.UPDATE_CHEQUE,
                    AuthPermissions.ADMIN_PERMISSION,
                    AuthPermissions.SUPER_ADMIN_PERMISSION
                  ) && (
                    <Link
                      component={RouterLink}
                      href={paths.dashboard.cheque.edit(currentCheque?._id ?? '')}
                    >
                      <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                        Edit
                      </Button>
                    </Link>
                  )}
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail label="Cheque Number" value={currentCheque?.chequeNumber} />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail
                    label="Account Number"
                    value={currentCheque?.accountNumber ?? '—'}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail
                    label="Amount"
                    value={fCurrencyRupees(currentCheque?.amountInCents ?? 0)}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail label="Bank" value={currentCheque?.bank} />
                </Grid>

                {currentCheque?.branchCode && (
                  <Grid xs={12} md={3}>
                    <CopyableDetail label="Branch Code" value={currentCheque.branchCode} />
                  </Grid>
                )}

                <Grid xs={12} md={3}>
                  <CopyableDetail label="Cheque Date" value={fAPIDate(currentCheque?.chequeDate)} />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail
                    label="Received Date"
                    value={fAPIDate(currentCheque?.receivedDate)}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail label="Payer Name" value={currentCheque?.payerName || '-'} />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail
                    label="Payer Account Number"
                    value={currentCheque?.payerAccountNumber || '-'}
                  />
                </Grid>

                <Divider
                  sx={{
                    my: 2.5,
                    typography: 'overline',
                    color: 'text.disabled',
                    '&:before, :after': {
                      borderTopStyle: 'dashed',
                    },
                  }}
                />

                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ my: 1 }}>
                    Operational Information
                  </Typography>
                </Grid>

                <Grid xs={12} md={3}>
                  {currentCheque?.invoice ? (
                    <ListItemText
                      primary="Customer"
                      secondary={
                        <a href={currentCheque.invoice} target="_blank" rel="noopener noreferrer">
                          {currentCheque.customer}
                        </a>
                      }
                      primaryTypographyProps={{ typography: 'body2' }}
                      secondaryTypographyProps={{
                        component: 'span',
                        color: 'text.disabled',
                      }}
                    />
                  ) : (
                    <CopyableDetail label="Customer" value={currentCheque?.customer} />
                  )}
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Sale Type"
                    secondary={
                      <Label
                        variant="soft"
                        color={
                          (currentCheque?.type === ChequeCustomerType.SALE && 'default') ||
                          (currentCheque?.type === ChequeCustomerType.WHOLESALE && 'info') ||
                          'default'
                        }
                      >
                        {currentCheque?.type?.split('_').join(' ')}
                      </Label>
                    }
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                      textTransform: 'capitalize',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail label="Holder" value={currentCheque?.holder} />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Label
                        variant="soft"
                        color={
                          (currentCheque?.status === ChequeStatus.IN_HAND && 'default') ||
                          (currentCheque?.status === ChequeStatus.HOLDING && 'warning') ||
                          (currentCheque?.status === ChequeStatus.BANKING && 'info') ||
                          (currentCheque?.status === ChequeStatus.PAID && 'success') ||
                          (currentCheque?.status === ChequeStatus.RETURN && 'error') ||
                          (currentCheque?.status === ChequeStatus.SETTLED && 'warning') ||
                          (currentCheque?.status === ChequeStatus.HANDOVER && 'info') ||
                          (currentCheque?.status === ChequeStatus.RECOVERING && 'warning') ||
                          (currentCheque?.status === ChequeStatus.RECOVERED && 'success') ||
                          (currentCheque?.status === ChequeStatus.FACTORING && 'secondary') ||
                          (currentCheque?.status === ChequeStatus.FACTORED && 'primary') ||
                          'default'
                        }
                      >
                        {currentCheque?.status.split('-').join(' ')}
                      </Label>
                    }
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary={
                      <Link
                        component={RouterLink}
                        href={paths.dashboard.user.view(currentCheque?.updatedBy._id ?? '')}
                      >
                        {`${currentCheque?.updatedBy.firstName} ${currentCheque?.updatedBy.lastName}`}
                      </Link>
                    }
                    secondary="Last Updated"
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <CopyableDetail label="Received Via" value={currentCheque?.receivedVia || '-'} />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Invoice"
                    secondary={
                      currentCheque?.invoice ? (
                        <a href={currentCheque.invoice} target="_blank" rel="noopener noreferrer">
                          View Invoice
                        </a>
                      ) : (
                        '-'
                      )
                    }
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12}>
                  <CopyableDetail
                    label="Note"
                    value={currentCheque?.note || '-'}
                    valueSx={{ whiteSpace: 'pre-wrap' }}
                  />
                </Grid>

                {currentCheque?.isFactored && (
                  <>
                    <Divider
                      sx={{
                        my: 2.5,
                        width: 1,
                        typography: 'overline',
                        color: 'text.disabled',
                        '&:before, :after': {
                          borderTopStyle: 'dashed',
                        },
                      }}
                    />

                    <Grid xs={12}>
                      <Typography variant="subtitle1" sx={{ my: 1 }}>
                        Factoring (LOLC)
                      </Typography>
                    </Grid>

                    <Grid xs={12} md={3}>
                      <ListItemText
                        primary="Factoring Status"
                        secondary={
                          <Label
                            variant="soft"
                            color={getFactoringStatusColor(currentCheque.factoringStatus)}
                          >
                            {currentCheque.factoringStatus}
                          </Label>
                        }
                        primaryTypographyProps={{ typography: 'body2' }}
                        secondaryTypographyProps={{
                          component: 'span',
                          color: 'text.disabled',
                        }}
                      />
                    </Grid>

                    <Grid xs={12} md={3}>
                      <ListItemText
                        primary="Payer"
                        secondary={`${currentCheque.payerName ?? '—'} (${
                          currentCheque.payerAccountNumber ?? '—'
                        })`}
                        primaryTypographyProps={{ typography: 'body2' }}
                        secondaryTypographyProps={{
                          component: 'span',
                          color: 'text.disabled',
                        }}
                      />
                    </Grid>

                    {currentCheque.factoringBatch && (
                      <Grid xs={12} md={3}>
                        <ListItemText
                          primary="Factoring Batch"
                          secondary={
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.chequeFactoring.batches.view(
                                currentCheque.factoringBatch
                              )}
                            >
                              View Batch
                            </Link>
                          }
                          primaryTypographyProps={{ typography: 'body2' }}
                          secondaryTypographyProps={{
                            component: 'span',
                            color: 'text.disabled',
                          }}
                        />
                      </Grid>
                    )}

                    {currentCheque.factoringReturnedAt && (
                      <Grid xs={12} md={3}>
                        <ListItemText
                          primary="Returned At"
                          secondary={fDateTime(currentCheque.factoringReturnedAt)}
                          primaryTypographyProps={{ typography: 'body2' }}
                          secondaryTypographyProps={{
                            component: 'span',
                            color: 'text.disabled',
                          }}
                        />
                      </Grid>
                    )}

                    {currentCheque.factoringSettlementDueAt && (
                      <Grid xs={12} md={3}>
                        <ListItemText
                          primary="Settlement Due"
                          secondary={fDateTime(currentCheque.factoringSettlementDueAt)}
                          primaryTypographyProps={{ typography: 'body2' }}
                          secondaryTypographyProps={{
                            component: 'span',
                            color:
                              currentCheque.factoringStatus === FactoringStatus.RETURNED
                                ? 'error.main'
                                : 'text.disabled',
                          }}
                        />
                      </Grid>
                    )}

                    {currentCheque.factoringClearedDate && (
                      <Grid xs={12} md={3}>
                        <ListItemText
                          primary="Cleared Date"
                          secondary={fDateTime(currentCheque.factoringClearedDate)}
                          primaryTypographyProps={{ typography: 'body2' }}
                          secondaryTypographyProps={{
                            component: 'span',
                            color: 'text.disabled',
                          }}
                        />
                      </Grid>
                    )}

                    {currentCheque.factoringSettledDate && (
                      <Grid xs={12} md={3}>
                        <ListItemText
                          primary="Settled Date"
                          secondary={fDateTime(currentCheque.factoringSettledDate)}
                          primaryTypographyProps={{ typography: 'body2' }}
                          secondaryTypographyProps={{
                            component: 'span',
                            color: 'text.disabled',
                          }}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}

            {tabValue === 'history' && currentCheque && (
              <Grid xs={12}>
                <ChequeTimeline
                  chequeId={currentCheque._id}
                  refreshKey={`${currentCheque.updatedAt}-${settlementRefreshKey}`}
                  showMoney={SETTLEMENT_STATUSES.includes(currentCheque.status)}
                />
              </Grid>
            )}

            {tabValue === 'payments' && (
              <Grid xs={12} mt={2}>
                {/* Payment Progress Summary */}
                <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Payment Progress
                  </Typography>
                  {loadingPayments ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Total Amount:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {fCurrencyRupees(currentCheque?.amountInCents ?? 0)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Total Paid:
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          {fCurrencyRupees(paymentTotal)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Remaining:
                        </Typography>
                        <Typography variant="body2" color="warning.main" fontWeight="bold">
                          {fCurrencyRupees((currentCheque?.amountInCents ?? 0) - paymentTotal)}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(paymentTotal / (currentCheque?.amountInCents ?? 1)) * 100}
                        sx={{ height: 8, borderRadius: 1, mt: 1 }}
                      />
                    </Stack>
                  )}
                </Box>

                {/* Add Payment Button (only for RECOVERING with remaining balance) */}
                {currentCheque?.status === ChequeStatus.RECOVERING &&
                  currentCheque.amountInCents - paymentTotal > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<Iconify icon="mingcute:add-line" />}
                        onClick={() => showPaymentDialog.onTrue()}
                      >
                        Add Payment
                      </Button>
                    </Box>
                  )}

                {/* Payment List */}
                <Typography variant="subtitle2" gutterBottom>
                  Payment History ({payments.length})
                </Typography>

                {loadingPayments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : payments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No payments recorded yet
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Stack spacing={1.5}>
                      {payments.map((payment) => (
                        <Card key={payment._id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="h6" color="primary">
                                {fCurrencyRupees(payment.amountInCents)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fDate(payment.paymentDate)}
                              </Typography>
                            </Stack>
                            {payment.note && (
                              <Typography variant="body2" color="text.secondary">
                                {payment.note}
                              </Typography>
                            )}
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="caption" color="text.disabled">
                                Added by {payment.createdBy.firstName} {payment.createdBy.lastName}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                {payment.paidDirectToSupplier && (
                                  <Label color="info" variant="soft">
                                    Paid to supplier directly
                                  </Label>
                                )}
                                {payment.recoveryHistory && (
                                  <Label color="success" variant="soft">
                                    Final Payment
                                  </Label>
                                )}
                              </Stack>
                            </Stack>
                          </Stack>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Grid>
            )}

            {tabValue === 'settlements' && currentCheque && (
              <Grid xs={12}>
                <SettlementTab
                  cheque={currentCheque}
                  refreshKey={settlementRefreshKey}
                  onChanged={refreshCheque}
                />
              </Grid>
            )}

            <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
              <Link component={RouterLink} href={paths.dashboard.cheque.root()}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              {hasPermission(
                authUser,
                'any',
                AuthPermissions.UPDATE_FACTORING_CHEQUE_STATUS,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) &&
                currentCheque?.factoringStatus === FactoringStatus.ADVANCE_RECEIVED && (
                  <>
                    <Button
                      variant="outlined"
                      color="success"
                      type="button"
                      onClick={() => showClearedDialog.onTrue()}
                    >
                      Mark Cleared
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      type="button"
                      onClick={() => showReturnedDialog.onTrue()}
                    >
                      Mark Returned
                    </Button>
                  </>
                )}

              {hasPermission(
                authUser,
                'any',
                AuthPermissions.UPDATE_FACTORING_CHEQUE_STATUS,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) &&
                currentCheque?.factoringStatus === FactoringStatus.RETURNED && (
                  <Button
                    variant="contained"
                    color="warning"
                    type="button"
                    onClick={() => showSettledDialog.onTrue()}
                  >
                    Mark Settled
                  </Button>
                )}

              {hasPermission(
                authUser,
                'any',
                AuthPermissions.UPDATE_CHEQUE_STATUS,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) &&
                currentCheque?.status !== ChequeStatus.RECOVERED &&
                currentCheque?.status !== ChequeStatus.RECOVERING &&
                currentCheque?.status !== ChequeStatus.FACTORING &&
                currentCheque?.status !== ChequeStatus.FACTORED && (
                  <Button variant="contained" type="button" onClick={() => confirm.onTrue()}>
                    Change Status
                  </Button>
                )}
            </Stack>
          </Card>
        </Grid>
      </Grid>
      {currentCheque && showClearedDialog.value && (
        <MarkClearedDialog
          cheque={currentCheque}
          isOpen={showClearedDialog.value}
          onClose={() => showClearedDialog.onFalse()}
          onSuccess={refreshCheque}
        />
      )}
      {currentCheque && showReturnedDialog.value && (
        <MarkReturnedDialog
          cheque={currentCheque}
          isOpen={showReturnedDialog.value}
          onClose={() => showReturnedDialog.onFalse()}
          onSuccess={refreshCheque}
        />
      )}
      {currentCheque && showSettledDialog.value && (
        <MarkSettledDialog
          cheque={currentCheque}
          isOpen={showSettledDialog.value}
          onClose={() => showSettledDialog.onFalse()}
          onSuccess={refreshCheque}
        />
      )}
      {currentCheque && (
        <UpdateChequeStatus
          currentCheque={currentCheque}
          isOpen={confirm.value}
          onClose={() => confirm.onFalse()}
        />
      )}
      {currentCheque && showPaymentDialog.value && (
        <AddPaymentDialog
          cheque={currentCheque}
          isOpen={showPaymentDialog.value}
          onClose={() => showPaymentDialog.onFalse()}
          onSuccess={async () => {
            // A direct-to-supplier payment also adds a settlement
            setSettlementRefreshKey((k) => k + 1);
            // Refresh payment data
            try {
              const [paymentsRes, totalRes] = await Promise.all([
                dispatch(getChequePayments({ chequeId: currentCheque._id })).unwrap(),
                dispatch(getChequePaymentTotal({ chequeId: currentCheque._id })).unwrap(),
              ]);
              setPayments(paymentsRes);
              setPaymentTotal(totalRes.totalPaid);
            } catch (error) {
              console.error('Failed to refresh payment data:', error);
            }
          }}
          remainingAmount={(currentCheque.amountInCents ?? 0) - paymentTotal}
        />
      )}
    </>
  );
}
